/**
 * FormCloseAndCreateDrawer
 *
 * Target-state and target-form-type picker for the close-&-create action owned
 * by `useFormCloseAndCreate`. Receives the permitted form-type ids and leaves
 * their label lookup to `CloseAndCreateFormTypeSelect` in the body, which
 * `destroyOnHidden` unmounts while closed — so this shell reads nothing from the
 * store and the form-type collection stays out of the action provider's render
 * path.
 */

import { Button, Drawer, Form, Select, Space, theme, Typography } from 'antd';
import type { FormStateId } from '#types';
import CloseAndCreateFormTypeSelect from './CloseAndCreateFormTypeSelect';

const { Text } = Typography;

const DRAWER_WIDTH_IN_SIZE_UNITS = 105;

type Option = {
  label: string;
  value: string;
};

type FormCloseAndCreateDrawerProps = {
  open: boolean;
  confirmLoading: boolean;
  allowedStateOptions: Option[];
  allowedFormTypeIds: string[];
  selectedState: FormStateId | null;
  selectedFormType: string | null;
  onChangeState: (value: FormStateId) => void;
  onChangeFormType: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function FormCloseAndCreateDrawer({
  open,
  confirmLoading,
  allowedStateOptions,
  allowedFormTypeIds,
  selectedState,
  selectedFormType,
  onChangeState,
  onChangeFormType,
  onCancel,
  onConfirm,
}: FormCloseAndCreateDrawerProps) {
  const { token } = theme.useToken();
  return (
    <Drawer
      title="Close & Create"
      open={open}
      onClose={onCancel}
      size={token.sizeUnit * DRAWER_WIDTH_IN_SIZE_UNITS}
      destroyOnHidden
      extra={(
        <Space>
          <Button
            onClick={onCancel}
            disabled={confirmLoading}
            aria-label="cancel-close-and-create"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onConfirm}
            loading={confirmLoading}
            disabled={confirmLoading || !selectedState || !selectedFormType}
            aria-label="confirm-close-and-create"
          >
            Confirm
          </Button>
        </Space>
      )}
    >
      <Form layout="vertical">
        <Form.Item label="Set form to" required>
          <Select
            aria-label="close-and-create-set-form-state"
            options={allowedStateOptions}
            value={selectedState ?? undefined}
            onChange={(value) => onChangeState(value as FormStateId)}
            placeholder="Select state"
          />
        </Form.Item>
        <Form.Item label="Create new form" required>
          <CloseAndCreateFormTypeSelect
            allowedFormTypeIds={allowedFormTypeIds}
            selectedFormType={selectedFormType}
            onChangeFormType={onChangeFormType}
          />
        </Form.Item>
      </Form>
      <Text type="secondary">
        This will close the current form and create a linked form in one operation.
      </Text>
    </Drawer>
  );
}
